import React, { useMemo, useState } from "react";
import { ReactComponent as IconCopy } from "@assets/Icons/IconCopy.svg";
import { TOKEN_PREFIX } from "./types";

/**
 * Where to send this token, and how.
 *
 * The URL here is `REACT_APP_CUSTOMER_API_URL` — CTX's public API, a
 * different service on a different host from the dashboard's own backend.
 * That distinction is the whole reason this component exists: the token is
 * issued by one service and spent at another, and nothing else on this page
 * mentions the second one.
 *
 * It pointed at the MCP endpoint until this was fixed, which was wrong twice
 * over — wrong host, and MCP keys are a different credential entirely.
 * Nothing about MCP belongs here.
 */

const KEY_PLACEHOLDER = `${TOKEN_PREFIX}YOUR_TOKEN`;

interface CopyableProps {
  value: string;
  label?: string;
  multiline?: boolean;
}

const Copyable: React.FC<CopyableProps> = ({
  value,
  label,
  multiline = false
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is unavailable over plain http and in some embedded
      // webviews. The value is on screen and selectable either way.
    }
  };

  return (
    <div className="rounded-md border border-semantic-border-secondary overflow-hidden">
      {label && (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-semantic-border-secondary">
          <span className="text-[11px] uppercase tracking-wide text-semantic-text-secondary">
            {label}
          </span>
          {copied && (
            <span className="text-[11px] text-semantic-text-success">
              Copied
            </span>
          )}
        </div>
      )}
      <div className="flex items-start gap-2 p-3">
        <pre
          className={`flex-1 text-xs text-semantic-text-primary ${
            multiline
              ? "whitespace-pre overflow-x-auto"
              : "break-all whitespace-pre-wrap"
          }`}
        >
          {value}
        </pre>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 p-1 rounded hover:bg-semantic-state-active transition-colors"
          title="Copy"
          aria-label={`Copy ${label ?? "value"}`}
        >
          <IconCopy width={16} height={16} />
        </button>
      </div>
    </div>
  );
};

interface HowToConnectProps {
  /**
   * The key to substitute into the example. Omitted outside the create
   * flow, where the raw value no longer exists — the example then shows a
   * placeholder.
   */
  rawKey?: string;
  /** Override the base URL. Defaults to REACT_APP_CUSTOMER_API_URL. */
  apiBaseUrlOverride?: string;
}

const HowToConnect: React.FC<HowToConnectProps> = ({
  rawKey,
  apiBaseUrlOverride
}) => {
  const baseUrl = (
    apiBaseUrlOverride ||
    process.env.REACT_APP_CUSTOMER_API_URL ||
    ""
  ).replace(/\/$/, "");
  const key = rawKey || KEY_PLACEHOLDER;

  // A real endpoint rather than an identity probe: the question someone has
  // right after being handed a token is "does this actually return my data",
  // and `/ports` answers it with something recognisable.
  const example = useMemo(
    () => `curl ${baseUrl}/ports \\\n  -H "Authorization: Bearer ${key}"`,
    [baseUrl, key]
  );

  return (
    <div className="space-y-4">
      <Copyable value={baseUrl} label="Base URL" />
      <Copyable value={`Authorization: Bearer ${key}`} label="Header" />
      <Copyable value={example} label="Try it" multiline />

      {!rawKey && (
        <p className="text-xs text-semantic-text-secondary">
          The example shows a placeholder. Generate a key and it fills in the
          real value.
        </p>
      )}
    </div>
  );
};

export default HowToConnect;
